import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Button from '../../../../components/Button';
import CircledIcon from '../../../../components/CircledIcon';
import Support from '../../../../components/Support';
import SupportMobile from '../../../../components/Support/SupportMobile';
import useModal from '../../../../hooks/useModal';
import { formatPrice } from '../../../../utils/utils';
import FederalTax from './FederalTax';
import StateTax from './StateTax';

export type StepProps = {
  data: any;
  collectStepData: (data: any) => void;
};

const RetireOnlineStep7 = ({ data, collectStepData }: StepProps) => {
  const [showStateTax, handleShowStateTax, handleCloseStateTax] = useModal();
  const [showFederalTax, handleShowFederalTax, handleCloseFederalTax] =
    useModal();

  const [federalTaxDataStorage, setFederalTaxDataStorage] = useState(
    data.federalTaxData
  );
  const [stateTaxDataStorage, setStateTaxDataStorage] = useState(
    data.stateTaxData
  );

  const handleSetFederalTaxData = (data: any) => {
    setFederalTaxDataStorage(data);
  };

  const handleSetStateData = (data: any) => {
    setStateTaxDataStorage(data);
  };

  const calculateTotalAmountStep3 = useMemo(() => {
    const { children, dependepts, additionalAmount } =
      federalTaxDataStorage.step3;

    let totalSum = 0;

    if (additionalAmount) {
      totalSum += additionalAmount;
    }

    if (children) {
      totalSum += children * 2000;
    }

    if (dependepts) {
      totalSum += dependepts * 500;
    }

    return totalSum;
  }, [federalTaxDataStorage.step3]);

  return (
    <Row className="p-0">
      <Col className="col-12 col-lg-9">
        <section className="pt-10">
          <div className="d-flex flex-row justify-content-between align-items-center">
            <h5 className={'text-sm-20-lg-24'}>Tax Withholding</h5>
            <div className={'text-sm-20-lg-24 d-lg-none'}>
              <SupportMobile>
                <div className="p-0 mt-4">
                  <span className="fw-bold text-caption">
                    Retirement Case Administrator
                  </span>
                  <div className="mt-4">
                    <CircledIcon
                      icon={light('user-headset')}
                      label="Jessica Wright"
                    />
                  </div>
                  <div className="mt-4">
                    <CircledIcon
                      icon={light('phone')}
                      label="1-800-888-8888 x 1234"
                    />
                  </div>
                </div>
              </SupportMobile>
            </div>
          </div>
        </section>
        <section className="mt-10 bg-white rounded-1 p-10">
          <Row className="m-0">
            <Col className="p-0 d-flex align-items-center">
              <h6 className="m-0 lh-xs">Federal Tax Withholding</h6>
            </Col>
            <Col className="p-0 d-flex justify-content-end">
              <Button
                variant="primary"
                size="sm"
                onClick={handleShowFederalTax}
              >
                Change
              </Button>
              <FederalTax
                show={showFederalTax}
                handleClose={handleCloseFederalTax}
                data={federalTaxDataStorage}
                collectData={handleSetFederalTaxData}
              />
            </Col>
          </Row>
          <Row className="m-0 pt-3 border-bottom">
            <Col className="p-0 py-3 col-lg-2">
              <span className="text-caption fw-normal lh-xs">Plan</span>
            </Col>
            <Col className="p-0 py-3 ps-10">
              <span className="text-caption fw-normal lh-xs">
                How much you’re withholding per check
              </span>
            </Col>
          </Row>
          <Row className="m-0 pt-3">
            <Col className="p-0 py-3 col-lg-2">
              <span className="text-caption fw-normal lh-xs">
                Company Pension Plan
              </span>
            </Col>
            <Col className="p-0 py-3 ps-10">
              <span className="text-caption fw-normal lh-xs">
                {`Calculate based on Head of Household, ${formatPrice(
                  calculateTotalAmountStep3
                )} Dependent Credits,
              ${formatPrice(
                federalTaxDataStorage.step4.otherIncome || 0
              )} Other Income, ${formatPrice(
                  federalTaxDataStorage.step4.deductions || 0
                )} Deductions, and ${formatPrice(
                  federalTaxDataStorage.step4.extraWithholdings || 0
                )} Extra Withholding.`}
              </span>
            </Col>
          </Row>
        </section>
        <section className="mt-10 bg-white rounded-1 p-10">
          <Row className="m-0">
            <Col className="p-0 d-flex align-items-center">
              <h6 className="m-0 lh-xs">State Tax Withholding</h6>
            </Col>
            <Col className="p-0 d-flex justify-content-end">
              <Button variant="primary" size="sm" onClick={handleShowStateTax}>
                Change
              </Button>
              <StateTax
                show={showStateTax}
                handleClose={handleCloseStateTax}
                data={stateTaxDataStorage}
                collectData={handleSetStateData}
              />
            </Col>
          </Row>
          <Row className="m-0 pt-3 border-bottom">
            <Col className="p-0 py-3 col-lg-2">
              <span className="text-caption fw-normal lh-xs">Plan</span>
            </Col>
            <Col className="p-0 py-3 ps-10 col-lg-2">
              <span className="text-caption fw-normal lh-xs">State</span>
            </Col>
            <Col className="p-0 py-3">
              <span className="text-caption fw-normal lh-xs">
                How much you’re withholding per check
              </span>
            </Col>
          </Row>
          <Row className="m-0 pt-3">
            <Col className="p-0 py-3 col-lg-2">
              <span className="text-caption fw-normal lh-xs">
                Company Pension Plan
              </span>
            </Col>
            <Col className="p-0 py-3 ps-10 col-lg-2">
              <span className="text-caption fw-normal lh-xs">
                {stateTaxDataStorage.state.label || 'VA'}
              </span>
            </Col>
            <Col className="p-0 py-3">
              <span className="text-caption fw-normal lh-xs">
                {`Calculate based on ${
                  stateTaxDataStorage.martialStatus.label || 'Single'
                } with ${
                  stateTaxDataStorage.numberOfAllowances || '0'
                } allowances plus an additional
              ${
                stateTaxDataStorage.additionalAmount
                  ? formatPrice(stateTaxDataStorage.additionalAmount)
                  : '0'
              }.`}
              </span>
            </Col>
          </Row>
        </section>
      </Col>
      <div className="d-none d-lg-flex justify-content-lg-end col-lg-3">
        <section className="mt-12">
          <Support>
            <div className="p-0 mt-4">
              <span className="fw-bold text-caption">
                Retirement Case Administrator
              </span>
              <div className="mt-4">
                <CircledIcon
                  icon={light('user-headset')}
                  label="Jessica Wright"
                />
              </div>
              <div className="mt-4">
                <CircledIcon
                  icon={light('phone')}
                  label="1-800-888-8888 x 1234"
                />
              </div>
            </div>
          </Support>
        </section>
      </div>
    </Row>
  );
};

export default RetireOnlineStep7;
